import type { ComponentPropsWithoutRef, ElementType } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/cn'

// Markdown in the console's tokens, for MPG AI answers. react-markdown never
// renders raw HTML, so model output cannot inject markup.

function tag<T extends ElementType>(Tag: T, className: string) {
  // Drops react-markdown's `node` prop so it never reaches the DOM.
  return function Styled({ node: _node, ...props }: ComponentPropsWithoutRef<T> & { node?: unknown }) {
    const Component = Tag as ElementType
    return <Component className={className} {...props} />
  }
}

const components = {
  h1: tag('h1', 'mb-2 mt-4 text-base font-semibold text-foreground first:mt-0'),
  h2: tag('h2', 'mb-2 mt-4 text-base font-semibold text-foreground first:mt-0'),
  h3: tag('h3', 'mb-1.5 mt-3 text-sm font-semibold text-foreground first:mt-0'),
  p: tag('p', 'mb-3 leading-relaxed last:mb-0'),
  ul: tag('ul', 'mb-3 list-disc space-y-1 pl-5'),
  ol: tag('ol', 'mb-3 list-decimal space-y-1 pl-5'),
  li: tag('li', 'leading-relaxed'),
  // Job codes and order numbers in an answer arrive as links to the order
  // page (the API's CodeLinker); they open in a new tab so the dashboard or
  // chat stays put (client 2026-10-27). react-markdown drops javascript: URLs.
  a: ({ node: _node, ...props }: ComponentPropsWithoutRef<'a'> & { node?: unknown }) => (
    <a className="font-medium text-primary underline-offset-2 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
  ),
  strong: tag('strong', 'font-semibold text-foreground'),
  em: tag('em', 'italic'),
  blockquote: tag('blockquote', 'mb-3 border-l-2 border-foreground/15 pl-3 italic text-foreground/70'),
  hr: tag('hr', 'my-4 border-foreground/10'),
  code: tag('code', 'rounded bg-foreground/5 px-1 py-0.5 text-xs'),
  pre: tag('pre', 'mb-3 overflow-x-auto rounded-lg bg-foreground/5 p-3 text-xs'),
  // Answers compare options in tables constantly. A wide one scrolls inside
  // its own box, never the page (375px rule).
  table: ({ node: _node, ...props }: ComponentPropsWithoutRef<'table'> & { node?: unknown }) => (
    <div className="mb-3 max-w-full overflow-x-auto rounded-lg border border-foreground/10">
      <table className="w-full border-collapse text-sm" {...props} />
    </div>
  ),
  thead: tag('thead', 'bg-foreground/5'),
  th: tag('th', 'whitespace-nowrap px-3 py-2 text-left text-xs font-semibold text-foreground/70'),
  td: tag('td', 'border-t border-foreground/10 px-3 py-2 align-top'),
}

export default function Markdown({ children, className }: { children?: string | null; className?: string }) {
  return (
    <div className={cn('min-w-0 text-sm text-foreground', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children || ''}
      </ReactMarkdown>
    </div>
  )
}
